"use client";

import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Mail, Globe, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";

// Placeholder data – replace with API when available
const MOCK_MAILBOXES = [
  { id: "mb-1", displayName: "admin@contoso.com" },
  { id: "mb-2", displayName: "sales@contoso.com" },
  { id: "mb-3", displayName: "support@contoso.com" },
  { id: "mb-4", displayName: "info@contoso.com" },
];

const MOCK_SITES = [
  { id: "site-1", displayName: "Human Resources" },
  { id: "site-2", displayName: "Projects" },
  { id: "site-3", displayName: "Marketing" },
  { id: "site-4", displayName: "Company" },
];

const SynchronizationPage = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<"exchange" | "sharepoint">("exchange");

  const [exchangeSyncAll, setExchangeSyncAll] = useState(true);
  const [exchangeSelectedIds, setExchangeSelectedIds] = useState<Set<string>>(new Set());
  const [exchangeLoading, setExchangeLoading] = useState(false);

  const [sharePointSyncAll, setSharePointSyncAll] = useState(true);
  const [sharePointSelectedIds, setSharePointSelectedIds] = useState<Set<string>>(new Set());
  const [sharePointLoading, setSharePointLoading] = useState(false);

  useEffect(() => {
    setExchangeLoading(true);
    // Simulate fetch – replace with real API
    const t1 = setTimeout(() => {
      setExchangeLoading(false);
    }, 400);
    return () => clearTimeout(t1);
  }, []);

  useEffect(() => {
    setSharePointLoading(true);
    const t2 = setTimeout(() => {
      setSharePointLoading(false);
    }, 400);
    return () => clearTimeout(t2);
  }, []);

  const toggleExchangeMailbox = (id: string, checked: boolean) => {
    setExchangeSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleSharePointSite = (id: string, checked: boolean) => {
    setSharePointSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const tabs = [
    { id: "exchange" as const, labelKey: "synchronization.tabExchange", icon: Mail },
    { id: "sharepoint" as const, labelKey: "synchronization.tabSharePoint", icon: Globe },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">{String(t("synchronization.title"))}</h1>
        <p className="text-sm text-muted-foreground mt-1">{String(t("synchronization.subtitle"))}</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <nav className="flex lg:flex-col gap-1 lg:w-52 shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors text-left",
                activeTab === tab.id
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-muted-foreground hover:bg-secondary"
              )}
            >
              <tab.icon className="h-4 w-4 shrink-0" />
              <span className="hidden lg:inline">{String(t(tab.labelKey))}</span>
            </button>
          ))}
        </nav>

        <div className="flex-1 space-y-6">
          {activeTab === "exchange" && (
            <div className="rounded-lg border border-border/50 bg-card p-5 shadow-sm space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium">{String(t("synchronization.exchangeSyncAll"))}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {String(t("synchronization.exchangeSyncAllDesc"))}
                  </p>
                </div>
                <Switch
                  checked={exchangeSyncAll}
                  onCheckedChange={setExchangeSyncAll}
                />
              </div>

              {!exchangeSyncAll && (
                <div className="pt-2 border-t border-border/50">
                  <h4 className="text-sm font-medium mb-1">
                    {String(t("synchronization.exchangeChooseMailboxes"))}
                  </h4>
                  <p className="text-xs text-muted-foreground mb-3">
                    {String(t("synchronization.exchangeChooseMailboxesDesc"))}
                  </p>
                  {exchangeLoading && (
                    <p className="text-sm text-muted-foreground py-2">
                      {String(t("synchronization.loadingMailboxes"))}
                    </p>
                  )}
                  {!exchangeLoading && MOCK_MAILBOXES.length === 0 && (
                    <p className="text-sm text-muted-foreground py-2">
                      {String(t("synchronization.noMailboxes"))}
                    </p>
                  )}
                  {!exchangeLoading && MOCK_MAILBOXES.length > 0 && (
                    <ul className="space-y-2">
                      {MOCK_MAILBOXES.map((mb) => (
                        <li
                          key={mb.id}
                          className="flex items-center gap-3 py-2 rounded-md hover:bg-secondary/50 px-2 -mx-2"
                        >
                          <Checkbox
                            id={`exchange-${mb.id}`}
                            checked={exchangeSelectedIds.has(mb.id)}
                            onCheckedChange={(checked) =>
                              toggleExchangeMailbox(mb.id, checked === true)
                            }
                          />
                          <label
                            htmlFor={`exchange-${mb.id}`}
                            className="text-sm cursor-pointer flex-1"
                          >
                            {mb.displayName}
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div className="pt-2">
                <Button size="sm">
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  {String(t("synchronization.save"))}
                </Button>
              </div>
            </div>
          )}

          {activeTab === "sharepoint" && (
            <div className="rounded-lg border border-border/50 bg-card p-5 shadow-sm space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium">{String(t("synchronization.sharePointSyncAll"))}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {String(t("synchronization.sharePointSyncAllDesc"))}
                  </p>
                </div>
                <Switch
                  checked={sharePointSyncAll}
                  onCheckedChange={setSharePointSyncAll}
                />
              </div>

              {!sharePointSyncAll && (
                <div className="pt-2 border-t border-border/50">
                  <h4 className="text-sm font-medium mb-1">
                    {String(t("synchronization.sharePointChooseSites"))}
                  </h4>
                  <p className="text-xs text-muted-foreground mb-3">
                    {String(t("synchronization.sharePointChooseSitesDesc"))}
                  </p>
                  {sharePointLoading && (
                    <p className="text-sm text-muted-foreground py-2">
                      {String(t("synchronization.loadingSites"))}
                    </p>
                  )}
                  {!sharePointLoading && MOCK_SITES.length === 0 && (
                    <p className="text-sm text-muted-foreground py-2">
                      {String(t("synchronization.noSites"))}
                    </p>
                  )}
                  {!sharePointLoading && MOCK_SITES.length > 0 && (
                    <ul className="space-y-2">
                      {MOCK_SITES.map((site) => (
                        <li
                          key={site.id}
                          className="flex items-center gap-3 py-2 rounded-md hover:bg-secondary/50 px-2 -mx-2"
                        >
                          <Checkbox
                            id={`sharepoint-${site.id}`}
                            checked={sharePointSelectedIds.has(site.id)}
                            onCheckedChange={(checked) =>
                              toggleSharePointSite(site.id, checked === true)
                            }
                          />
                          <label
                            htmlFor={`sharepoint-${site.id}`}
                            className="text-sm cursor-pointer flex-1"
                          >
                            {site.displayName}
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div className="pt-2">
                <Button size="sm">
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  {String(t("synchronization.save"))}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SynchronizationPage;
